import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';
import RegistryCombobox from '@/components/RegistryCombobox';

const EMPTY = {
  name: '',
  type: '',
  serialNumber: '',
  manufacturer: '',
  supplier: '',
  operator: '',
  notes: '',
};

const EMPTY_PROBE = {
  code: 'S1',
  name: 'Sonda',
  probeTypeId: '',
  unitId: '',
  rangeMin: '0',
  rangeMax: '',
  methodologyId: '',
  probeRead: 'digital',
};

const REQUIRED = [
  ['name', 'Název'],
  ['type', 'Typ'],
  ['serialNumber', 'Výrobní číslo'],
  ['manufacturer', 'Výrobce'],
  ['supplier', 'Dodavatel'],
  ['operator', 'Provozovatel'],
];

/**
 * Inline dialog: nový přístroj (vč. inline založení výrobce/dodavatele/provozovatele)
 * + jeho sonda (měřicí kanál — metodika určuje veličinu). „Na jeden zátah" z objednávky.
 * Po vytvoření zavolá `onCreated(instrument)`.
 */
export default function InstrumentFormDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ ...EMPTY });
  const [probe, setProbe] = useState({ ...EMPTY_PROBE });
  const [companies, setCompanies] = useState([]);
  const [instTypes, setInstTypes] = useState([]);
  const [probeTypes, setProbeTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [methodologies, setMethodologies] = useState([]);
  const [saving, setSaving] = useState(false);

  const reloadCompanies = () => api.get('/companies').then((r) => setCompanies(r.data)).catch(() => {});

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY });
    setProbe({ ...EMPTY_PROBE });
    reloadCompanies();
    api.get('/instrument-types').then((r) => setInstTypes(r.data)).catch(() => {});
    api.get('/probe-types').then((r) => setProbeTypes(r.data)).catch(() => {});
    api.get('/units').then((r) => setUnits(r.data)).catch(() => {});
    api.get('/methodologies').then((r) => setMethodologies((r.data || []).filter((m) => m.verified))).catch(() => {});
  }, [open]);

  const hasAnyFlags = companies.some((c) => c.isManufacturer || c.isSupplier || c.isOperator);
  const filterByFlag = (flag) => (hasAnyFlags ? companies.filter((c) => c[flag]) : companies);

  const selectedTypeGroup = instTypes.find((t) => t.name === form.name);
  const availableTypes = selectedTypeGroup?.types || [];

  // Metodika sondy určuje laboratoř/veličinu → filtruje jednotky a typy sond.
  const selMeth = methodologies.find((m) => m.id === probe.methodologyId);
  const probeLab = selMeth?.laboratory || '';
  const unitOptions = probeLab ? units.filter((u) => !u.quantity || u.quantity === probeLab) : units;
  const probeTypeOptions = probeLab ? probeTypes.filter((p) => !p.laboratory || p.laboratory === probeLab) : probeTypes;

  const missing = REQUIRED.filter(([f]) => !String(form[f] || '').trim()).map(([, label]) => label);
  const probeReady = probe.methodologyId && probe.probeTypeId && probe.unitId
    && probe.code.trim() && probe.name.trim()
    && probe.rangeMin !== '' && probe.rangeMax !== ''
    && parseFloat(probe.rangeMin) < parseFloat(probe.rangeMax);
  const canSave = missing.length === 0 && probeReady;

  // Inline založení firmy s příslušným flagem + rovnou výběr.
  const createCompany = async (field, flag, name) => {
    if (!name) return;
    try {
      const res = await api.post('/companies', { name, [flag]: true });
      await reloadCompanies();
      setForm((f) => ({ ...f, [field]: res.data.id }));
      toast.success(`Firma „${name}" vytvořena`);
    } catch (err) {
      toastApiError(err);
    }
  };

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const res = await api.post('/instruments', form);
      const inst = res.data;
      // Sonda — bez ní by přístroj nešel kalibrovat (send-to-calibration zakládá
      // kalibraci per sonda s metodikou).
      try {
        await api.post('/probes', {
          instrumentId: inst.id,
          code: probe.code.trim(),
          name: probe.name.trim(),
          probeTypeId: probe.probeTypeId,
          unitId: probe.unitId,
          rangeMin: parseFloat(probe.rangeMin),
          rangeMax: parseFloat(probe.rangeMax),
          methodologyId: probe.methodologyId,
          probeRead: probe.probeRead,
        });
        toast.success('Přístroj i sonda vytvořeny');
      } catch (probeErr) {
        toastApiError(probeErr, 'Přístroj vytvořen, ale sondu se nepodařilo založit — doplňte ji v detailu přístroje.');
      }
      onCreated?.(inst);
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const companyField = (field, flag, label) => {
    const list = filterByFlag(flag);
    const value = form[field] || '';
    const legacyText = value && !list.find((c) => c.id === value) ? value : '';
    return (
      <div className="ct-form-field">
        <label className="ct-form-label">{label} *</label>
        <RegistryCombobox
          testid={`instrument-dialog-${field}`}
          value={value}
          displayLabel={legacyText}
          items={list}
          getKey={(c) => c.id}
          getLabel={(c) => c.name}
          onSelect={(c) => setForm({ ...form, [field]: c.id })}
          onCreateNew={(name) => createCompany(field, flag, name)}
          placeholder="— vyberte / napište nový —"
          createLabel="Vytvořit firmu"
        />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ background: 'var(--ct-surface)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}>
        <DialogHeader>
          <DialogTitle>Nový přístroj + sonda</DialogTitle>
          <DialogDescription>
            Vytvoří přístroj (vč. nového výrobce, když chybí) a jeho sondu. Rovnou se nabídne do objednávky.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="ct-form-field">
            <label className="ct-form-label">Název přístroje *</label>
            <select
              data-testid="instrument-dialog-name"
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value, type: '' })}
              className="h-9 text-sm rounded-md border px-3 w-full"
              style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
            >
              <option value="">— Vyberte —</option>
              {instTypes.map((t) => (
                <option key={t.id} value={t.name}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="ct-form-field">
            <label className="ct-form-label">Typ *</label>
            {!form.name ? (
              <div className="h-9 flex items-center px-3 rounded-md text-sm" style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text-disabled)' }}>
                Nejdřív vyberte název
              </div>
            ) : availableTypes.length === 0 ? (
              <Input value={form.type || ''} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Volný text" className="h-9 text-sm" />
            ) : (
              <select
                value={form.type || ''}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="h-9 text-sm rounded-md border px-3 w-full"
                style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
              >
                <option value="">— Vyberte typ —</option>
                {availableTypes.map((t, i) => (
                  <option key={i} value={t.typeName}>{t.typeName}{t.note ? ` (${t.note})` : ''}</option>
                ))}
              </select>
            )}
          </div>
          <div className="ct-form-field col-span-2">
            <label className="ct-form-label">Výrobní číslo *</label>
            <Input value={form.serialNumber || ''} onChange={(e) => setForm({ ...form, serialNumber: e.target.value })} className="h-9 text-sm font-mono" />
          </div>
          {companyField('manufacturer', 'isManufacturer', 'Výrobce')}
          {companyField('supplier', 'isSupplier', 'Dodavatel')}
          {companyField('operator', 'isOperator', 'Provozovatel')}
          <div className="ct-form-field">
            <label className="ct-form-label">Poznámka</label>
            <Input value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-9 text-sm" />
          </div>
        </div>

        {/* Sonda (měřicí kanál) */}
        <div className="mt-2 pt-3 border-t" style={{ borderColor: 'var(--ct-border)' }}>
          <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--ct-accent)' }}>Sonda (měřicí kanál)</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="ct-form-field col-span-2">
              <label className="ct-form-label">Metodika * <span className="font-normal" style={{ color: 'var(--ct-text-disabled)' }}>(určuje veličinu)</span></label>
              <select
                data-testid="probe-dialog-methodology"
                value={probe.methodologyId}
                onChange={(e) => setProbe({ ...probe, methodologyId: e.target.value, probeTypeId: '', unitId: '' })}
                className="h-9 text-sm rounded-md border px-3 w-full"
                style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
              >
                <option value="">— Vyberte metodiku —</option>
                {methodologies.map((m) => (
                  <option key={m.id} value={m.id}>{m.code} — {m.title} ({m.laboratory})</option>
                ))}
              </select>
            </div>
            <div className="ct-form-field">
              <label className="ct-form-label">Typ sondy *</label>
              <select
                value={probe.probeTypeId}
                onChange={(e) => setProbe({ ...probe, probeTypeId: e.target.value })}
                disabled={!probe.methodologyId}
                className="h-9 text-sm rounded-md border px-3 w-full disabled:opacity-50"
                style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
              >
                <option value="">— Vyberte —</option>
                {probeTypeOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="ct-form-field">
              <label className="ct-form-label">Jednotka *</label>
              <select
                value={probe.unitId}
                onChange={(e) => setProbe({ ...probe, unitId: e.target.value })}
                disabled={!probe.methodologyId}
                className="h-9 text-sm rounded-md border px-3 w-full disabled:opacity-50"
                style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
              >
                <option value="">— Vyberte —</option>
                {unitOptions.map((u) => (
                  <option key={u.id} value={u.id}>{u.symbol} — {u.description}</option>
                ))}
              </select>
            </div>
            <div className="ct-form-field">
              <label className="ct-form-label">Rozsah od *</label>
              <Input type="number" value={probe.rangeMin} onChange={(e) => setProbe({ ...probe, rangeMin: e.target.value })} className="h-9 text-sm font-mono" />
            </div>
            <div className="ct-form-field">
              <label className="ct-form-label">Rozsah do *</label>
              <Input type="number" value={probe.rangeMax} onChange={(e) => setProbe({ ...probe, rangeMax: e.target.value })} className="h-9 text-sm font-mono" />
            </div>
            <div className="ct-form-field">
              <label className="ct-form-label">Kód sondy *</label>
              <Input value={probe.code} onChange={(e) => setProbe({ ...probe, code: e.target.value })} className="h-9 text-sm font-mono" />
            </div>
            <div className="ct-form-field">
              <label className="ct-form-label">Odečet *</label>
              <select
                value={probe.probeRead}
                onChange={(e) => setProbe({ ...probe, probeRead: e.target.value })}
                className="h-9 text-sm rounded-md border px-3 w-full"
                style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
              >
                <option value="digital">digital</option>
                <option value="analog">analog</option>
              </select>
            </div>
          </div>
        </div>

        <DialogFooter>
          {!canSave && (
            <span className="text-xs mr-auto self-center" style={{ color: 'var(--ct-text-disabled)' }}>
              {missing.length > 0 ? `Chybí: ${missing.join(', ')}` : 'Doplňte sondu (metodika, typ, jednotka, rozsah)'}
            </span>
          )}
          <Button onClick={() => onOpenChange(false)} className="ct-btn-secondary">Zrušit</Button>
          <Button data-testid="instrument-dialog-save" onClick={submit} disabled={!canSave || saving} className="ct-btn-primary">
            {saving ? 'Vytvářím…' : 'Vytvořit přístroj + sondu'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
